"use client";

/**
 * Phase 9 (origin) / Phase 11 Plan 11-05 (retired bundle path) — Homepage
 * promo block for the "We Set It Up For You" service (UI-SPEC §2).
 *
 * Mounted between the trust strip and the documents grid in
 * app/(marketing)/page.tsx. Wrapped in
 * useAppFeatureFlag('setup_service') — when off, renders nothing
 * (the documents grid moves up; no visual gap).
 *
 * Phase 11 Plan 11-05 retired the free-tier `CheckoutSummary` bundle path.
 * The CTA now uniformly fires the server-action redirect to Stripe Checkout
 * for logged-out + free buyers; the existing-Pro D-06 path is preserved
 * inside the CTA button itself.
 *
 * Auth state is resolved server-side and passed as a prop (Pitfall 5 /
 * T-AUTH-RACE-MARKETING).
 *
 * Analytics:
 * - `setup_service_viewed` fires once on first ≥50% IntersectionObserver
 *   intersection (useRef guard prevents re-fires on scroll-back).
 * - `setup_service_cta_clicked` fires from <SetupServiceCtaButton>.
 */

import { useEffect, useRef } from "react";
import Link from "next/link";
import { useAppFeatureFlag } from "@/components/feature-flags/feature-flags-provider";

import { HOMEPAGE_BLOCK } from "@/lib/setup-service-copy";
import {
  trackSetupServiceEvent,
  deriveDeviceType,
} from "@/lib/setup-service-analytics";
import {
  SetupServiceCtaButton,
  type SetupServiceAuthState,
} from "@/components/setup-service/setup-service-cta-button";

export interface SetupServiceHomepageBlockProps {
  /** Server-resolved Clerk auth + plan + setup-service status. */
  authState: SetupServiceAuthState;
  /**
   * Phase 9 prop kept in the type for source-compat with callers. Phase 11
   * Plan 11-05 retired the bundle-checkout path so this is no longer read.
   * Will be removed in a future cleanup once external callers migrate.
   */
  defaultPriceId?: string;
}

export function SetupServiceHomepageBlock(props: SetupServiceHomepageBlockProps) {
  const { authState } = props;

  const flagEnabled = useAppFeatureFlag("setup_service");

  const sectionRef = useRef<HTMLElement | null>(null);
  const viewedFiredRef = useRef(false);

  useEffect(() => {
    if (flagEnabled !== true) return;
    const node = sectionRef.current;
    if (!node || typeof window === "undefined" || !("IntersectionObserver" in window)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && !viewedFiredRef.current) {
            viewedFiredRef.current = true;
            trackSetupServiceEvent("setup_service_viewed", {
              source_page: "homepage",
              device_type: deriveDeviceType(),
              plan_selected: null,
            });
            observer.disconnect();
          }
        }
      },
      // F-09-03 — include 0 so the callback fires on initial mount even when
      // the section is already 100% intersecting. Threshold-crossing
      // semantics mean a single 0.5 threshold can miss first paint; the
      // >=0.5 guard inside the callback still enforces the visibility floor.
      { threshold: [0, 0.5] },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [flagEnabled]);

  if (flagEnabled !== true) return null;

  return (
    <section ref={sectionRef} className="bg-surface-muted px-4 py-16 sm:px-6 sm:py-20">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="font-heading text-[28px] font-extrabold text-navy">
          {HOMEPAGE_BLOCK.heading}
        </h2>
        <p className="mt-3 text-[15px] leading-relaxed text-muted">{HOMEPAGE_BLOCK.body}</p>

        <ul className="mt-6 flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:gap-6 sm:divide-x sm:divide-border">
          {HOMEPAGE_BLOCK.bullets.map((bullet, idx) => (
            <li
              key={bullet}
              className={`text-sm text-muted ${idx > 0 ? "sm:pl-6" : ""}`}
            >
              {bullet}
            </li>
          ))}
        </ul>

        <div className="mt-8 flex justify-center">
          <div className="w-full max-w-xs">
            <SetupServiceCtaButton
              location="homepage"
              authState={authState}
              planSelected={null}
              ctaLabel={HOMEPAGE_BLOCK.cta}
            />
          </div>
        </div>

        {/* G3-8d — secondary "talk to us first" path to the lead form on
            /setup-for-you. The £249 self-serve CTA above stays primary. */}
        <p className="mt-4 text-sm text-muted">
          Prefer to talk it through first?{" "}
          <Link
            href="/setup-for-you#talk-to-us"
            onClick={() =>
              trackSetupServiceEvent("setup_service_cta_clicked", {
                source_page: "homepage",
                device_type: deriveDeviceType(),
                plan_selected: null,
                cta_label: "talk_to_us_link",
              })
            }
            className="font-semibold text-brand-700 underline hover:text-accent-dark"
          >
            Talk to us
          </Link>
        </p>
      </div>
    </section>
  );
}
